'use client';

import { useAuthStore } from '@/hooks/use-auth';
import type { TourSurface } from './registry';

/**
 * Client helpers for the tour-author iframe (tech-spec-universal-tour-authoring,
 * Tier 4, Task 13). The author editor embeds a role surface in a same-origin
 * iframe with `?__tourAuthor&surface=<surface>`. Inside that frame the app must
 * boot AS the fixed demo user — NOT via the admin refresh cookie (which would
 * render admin data) — so its tRPC calls return real synthetic data.
 *
 * R2-#1 (deadlock): the demo boot MUST run at/above the blocking `AuthProvider`
 * gate (which spins until `isAuthenticated`), because `TourHost` mounts BELOW
 * that gate — seeding auth in `TourHost` would spin forever. Hence these helpers
 * are consumed by `SessionHydrator` + `AuthProvider`, not `TourHost`.
 */

/** True when the current document is the tour-author iframe (param + iframed). */
export function isAuthorFrame(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const hasParam = new URLSearchParams(window.location.search).has('__tourAuthor');
    const iframed = window.top !== window.self;
    return hasParam && iframed;
  } catch {
    return false;
  }
}

/** The `surface` URL param of the author iframe (null when absent/invalid). */
export function getAuthorSurface(): TourSurface | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('surface');
    if (raw === 'teacher' || raw === 'parent' || raw === 'admin' || raw === 'student') {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Mint a demo-user access token for the frame's surface and seed the in-memory
 * auth store with it. In-memory ONLY — NO cookies are set, so the parent admin
 * tab (a separate document) is untouched. Returns true on success.
 *
 * This is also the RE-MINT path (Task 13 / F2): when the short-lived demo token
 * expires or any demo call 401s, the fetch wrapper calls this instead of
 * `/api/auth/refresh` (which would return an ADMIN token and silently revert the
 * iframe). The endpoint authenticates the re-mint via the `tour_author` capability
 * cookie (R2-#8).
 */
export async function bootDemoSession(): Promise<boolean> {
  const surface = getAuthorSurface();
  if (!surface) return false;
  try {
    const res = await fetch('/api/tour-author-session/demo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ surface }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as {
      accessToken?: string;
      user?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: TourSurface;
        timezone: string;
        preferredLanguage: string;
      };
    };
    if (!data.accessToken || !data.user) return false;
    useAuthStore.getState().setAuth(data.accessToken, data.user as never);
    return true;
  } catch {
    return false;
  }
}
